import { Link } from "@tanstack/react-router";
import dayjs from "dayjs";
import {
  BuildingIcon,
  FilterIcon,
  FlaskConicalIcon,
  LockIcon,
  MailIcon,
  XIcon,
} from "lucide-react";
import { useCallback, useState } from "react";
import useDebounce from "react-use/lib/useDebounce";
import useTitle from "react-use/lib/useTitle";

import { trpc } from "@blis/trpc/react";

import { SubHeader } from "@/components/SubHeader";
import { UserAvatar } from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { EmptyState, EmptyStateDescription } from "@/components/ui/empty-state";
import { Pagination } from "@/components/ui/pagination";
import { Popover } from "@/components/ui/popover";
import { SearchField, SearchInput } from "@/components/ui/search-field";
import { Skeleton } from "@/components/ui/skeleton";
import { Tag, TagGroup, TagList } from "@/components/ui/tag-group";
import { Text } from "@/components/ui/text";
import { useAuth } from "@/features/auth/hooks";
import { format } from "@/lib/utils";
import { useNavLocationState } from "@/utils/useNavStack";

type CommentFilters = {
  client: number[];
  labs: number[];
  testType: ("SEROLOGY" | "CLINICAL" | "HLA" | "MICROBIOLOGY")[];
};

const defaultFilters: CommentFilters = {
  client: [],
  labs: [],
  testType: [],
};

const typeOptions = [
  { label: "Serology", value: "SEROLOGY" as const },
  { label: "Clinical", value: "CLINICAL" as const },
  { label: "HLA", value: "HLA" as const },
  { label: "Microbiology", value: "MICROBIOLOGY" as const },
];

const PAGE_SIZE = 50;

export function CommentsPage() {
  useTitle("Comments");
  const [filters, setFiltersRaw] = useState<CommentFilters>(defaultFilters);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useDebounce(() => setSearch(searchInput), 250, [searchInput]);

  const setFilters: React.Dispatch<React.SetStateAction<CommentFilters>> =
    useCallback((action) => {
      setFiltersRaw(action);
      setPage(1);
    }, []);

  const hasFilters =
    filters.client.length > 0 ||
    filters.labs.length > 0 ||
    filters.testType.length > 0;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <SubHeader>
        <SubHeader.Left>
          <Breadcrumbs>
            <Breadcrumb href={{ to: "/" }}>Dashboard</Breadcrumb>
            <Breadcrumb>Comments</Breadcrumb>
          </Breadcrumbs>
        </SubHeader.Left>
        <SubHeader.Right className="gap-2">
          <SearchField
            aria-label="Search comments"
            value={searchInput}
            onChange={(value) => {
              setSearchInput(value);
              setPage(1);
            }}
            className="w-48"
          >
            <SearchInput placeholder="Search..." />
          </SearchField>
          <CommentFilterPopover
            filters={filters}
            setFilters={setFilters}
            hasFilters={hasFilters}
          />
        </SubHeader.Right>
      </SubHeader>
      {hasFilters && (
        <CommentFilterBar filters={filters} setFilters={setFilters} />
      )}
      <CommentsList
        filters={filters}
        search={search}
        page={page}
        onPageChange={setPage}
      />
    </div>
  );
}

function CommentFilterPopover({
  filters,
  setFilters,
  hasFilters,
}: {
  filters: CommentFilters;
  setFilters: React.Dispatch<React.SetStateAction<CommentFilters>>;
  hasFilters: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState("");
  const [search, setSearch] = useState("");

  const { user } = useAuth();
  const { data: primaryLabs } = trpc.orgs.primaryLabs.useQuery();
  const { data: orgs } = trpc.orgs.primaryOrgsList.useQuery();

  const allowedOrgs = (user?.profile.allowedOrgs as number[]) || [];
  const labRole = user?.hasRole([
    "blis_lab_admin",
    "blis_lab_manager",
    "blis_lab_tech",
    "blis_lab_assistant",
    "blis_lab_readonly",
  ]);
  const showClientFilter = labRole || allowedOrgs.length > 0;
  let orgOptions = orgs || [];
  if (!labRole && allowedOrgs.length > 0) {
    orgOptions = (orgs || []).filter(
      (o) => allowedOrgs.includes(o.id) || o.id === user?.profile.org?.id,
    );
  }

  const onOpenChange = (flag: boolean) => {
    if (!flag) {
      setTimeout(() => {
        setPage("");
      }, 200);
    }
    setSearch("");
    setOpen(flag);
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={onOpenChange}>
      <Button size="sm" variant="outline" onPress={() => setOpen(true)}>
        <FilterIcon data-slot="icon" />
        Filter
      </Button>
      <Popover
        className="p-0 sm:min-w-[220px] sm:max-w-[300px]"
        placement="bottom end"
      >
        <Dialog
          className="p-0! outline-hidden"
          aria-label="Filter comments"
          hideCloseButton
        >
          <Command
            className="**:[[cmdk-input]]:h-12 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-2 [&_[cmdk-item]>svg]:size-4 [&_[cmdk-item]>svg]:mr-2"
            onKeyDown={(e) => {
              if (e.key === "Escape" && page) {
                e.preventDefault();
                e.stopPropagation();
                setPage("");
                setSearch("");
              }
              if (e.key.startsWith("Arrow")) {
                e.stopPropagation();
              }
            }}
          >
            <CommandInput
              placeholder="Filter..."
              value={search}
              onValueChange={setSearch}
              // biome-ignore lint/complexity/noUselessFragments: required
              icon={<></>}
            />
            <CommandEmpty>No filter found</CommandEmpty>
            <CommandList>
              <CommandGroup>
                {!page && (
                  <>
                    {showClientFilter && (
                      <CommandItem onSelect={() => setPage("client")}>
                        <BuildingIcon />
                        Client
                      </CommandItem>
                    )}
                    <CommandItem onSelect={() => setPage("lab")}>
                      <BuildingIcon />
                      Lab
                    </CommandItem>
                    <CommandItem onSelect={() => setPage("testType")}>
                      <FlaskConicalIcon />
                      Test type
                    </CommandItem>
                  </>
                )}
                {page === "client" &&
                  orgOptions.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.id}
                      value={opt.name}
                      checked={filters.client.includes(opt.id)}
                      onCheckedChanged={(flag) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            client: [...s.client, opt.id],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            client: s.client.filter((id) => id !== opt.id),
                          }));
                        }
                      }}
                    >
                      {opt.name}
                    </FilterCheckboxItem>
                  ))}
                {page === "lab" &&
                  primaryLabs?.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.id}
                      value={opt.name}
                      checked={filters.labs.includes(opt.id)}
                      onCheckedChanged={(flag) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            labs: [...s.labs, opt.id],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            labs: s.labs.filter((id) => id !== opt.id),
                          }));
                        }
                      }}
                    >
                      {opt.name}
                    </FilterCheckboxItem>
                  ))}
                {page === "testType" &&
                  typeOptions.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.value}
                      value={opt.label}
                      checked={filters.testType.includes(opt.value)}
                      onCheckedChanged={(flag) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            testType: [...s.testType, opt.value],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            testType: s.testType.filter((t) => t !== opt.value),
                          }));
                        }
                      }}
                    >
                      <span className="capitalize">{opt.label}</span>
                    </FilterCheckboxItem>
                  ))}
              </CommandGroup>
              {hasFilters && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setFilters(defaultFilters);
                        onOpenChange(false);
                      }}
                    >
                      <XIcon className="size-4" />
                      <span>Clear filters</span>
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

function FilterCheckboxItem({
  checked,
  onCheckedChanged,
  children,
  value,
}: {
  checked: boolean;
  onCheckedChanged: (flag: boolean) => void;
  children?: React.ReactNode;
  value: string;
}) {
  return (
    <CommandItem
      onSelect={() => onCheckedChanged(!checked)}
      className="flex items-center gap-2"
      value={value}
    >
      <Checkbox
        isSelected={checked}
        className="border-muted-foreground"
        onChange={(flag) => onCheckedChanged(!!flag)}
        excludeFromTabOrder
      >
        {children}
      </Checkbox>
    </CommandItem>
  );
}

function CommentFilterBar({
  filters,
  setFilters,
}: {
  filters: CommentFilters;
  setFilters: React.Dispatch<React.SetStateAction<CommentFilters>>;
}) {
  const { data: primaryLabs } = trpc.orgs.primaryLabs.useQuery();
  const { data: orgs } = trpc.orgs.primaryOrgsList.useQuery();

  const onRemove = (keys: Set<React.Key>) => {
    for (const key of keys) {
      const [filterKey, filterValue] = key.toString().split("-");
      if (filterKey === "client") {
        setFilters((s) => ({
          ...s,
          client: s.client.filter((id) => id !== Number(filterValue)),
        }));
      } else if (filterKey === "labs") {
        setFilters((s) => ({
          ...s,
          labs: s.labs.filter((id) => id !== Number(filterValue)),
        }));
      } else if (filterKey === "testType") {
        setFilters((s) => ({
          ...s,
          testType: s.testType.filter((t) => t !== filterValue),
        }));
      }
    }
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b">
      <TagGroup
        onRemove={onRemove}
        aria-label="Comment filters"
        color="primary"
        variant="outline"
      >
        <TagList className="flex items-center flex-wrap gap-2">
          {filters.client.map((clientId) => (
            <Tag key={`client-${clientId}`} id={`client-${clientId}`}>
              Client: {orgs?.find((o) => o.id === clientId)?.name || "-"}
            </Tag>
          ))}
          {filters.labs.map((labId) => (
            <Tag key={`labs-${labId}`} id={`labs-${labId}`}>
              Lab: {primaryLabs?.find((l) => l.id === labId)?.name || "-"}
            </Tag>
          ))}
          {filters.testType.map((type) => (
            <Tag key={`testType-${type}`} id={`testType-${type}`}>
              Type: {type}
            </Tag>
          ))}
        </TagList>
      </TagGroup>
      <Button
        size="sm"
        variant="ghost"
        onPress={() => setFilters(defaultFilters)}
      >
        Clear
      </Button>
    </div>
  );
}

function CommentsList({
  filters,
  search,
  page,
  onPageChange,
}: {
  filters: CommentFilters;
  search: string;
  page: number;
  onPageChange: (page: number) => void;
}) {
  const { data, isLoading, isFetching } = trpc.metrics.commentsList.useQuery({
    take: PAGE_SIZE,
    skip: (page - 1) * PAGE_SIZE,
    search,
    client: filters.client,
    labs: filters.labs,
    testType: filters.testType,
  });

  const pageCount = Math.ceil((data?.totalCount ?? 0) / PAGE_SIZE);

  const { user } = useAuth();
  const isLabRole = user?.isLabRole();
  const { href, label } = useNavLocationState();

  const comments = data?.items ?? [];
  const showSkeleton = isLoading || (isFetching && comments.length === 0);

  return (
    <>
      {showSkeleton ? (
        <div className="flex-1 overflow-auto">
          <div className="divide-y">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={`skeleton-${i}`}
                className="flex items-start gap-3 px-4 py-3"
              >
                <Skeleton shape="circle" className="size-8 mt-0.5" />
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : comments.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState>
            <EmptyStateDescription>No comments found</EmptyStateDescription>
          </EmptyState>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <div className="divide-y">
            {comments.map((comment) => {
              const commenter =
                comment.createdById === user?.profile.id
                  ? "You"
                  : comment.createdBy?.username || "Someone";

              return (
                <Link
                  key={comment.id}
                  to="/accessions/$id"
                  params={{ id: comment.accessionId }}
                  state={{ href, selected: comment.accessionId, label }}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-muted/50 transition-colors outline-hidden focus-visible:bg-muted/50"
                >
                  <div className="group/avatar relative shrink-0 mt-0.5">
                    <UserAvatar user={comment.createdBy} />
                    <CommenterHoverCard
                      name={commenter}
                      email={comment.createdBy?.email}
                      orgName={comment.createdBy?.org?.name}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Text size="2" className="font-medium">
                        {commenter}
                      </Text>
                      <Text muted size="1" title={format(comment.createdAt)}>
                        {dayjs(comment.createdAt).fromNow()}
                      </Text>
                      {comment.isInternal && isLabRole && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                          <LockIcon className="size-3" />
                          Internal
                        </span>
                      )}
                    </div>
                    <pre className="mt-1 whitespace-pre-wrap font-sans text-sm text-foreground line-clamp-3">
                      {comment.text}
                    </pre>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <Badge size="sm">{comment.accessionId}</Badge>
                      {comment.accession.subject?.caseId && (
                        <Badge variant="outline" size="sm">
                          {comment.accession.subject.caseId}
                        </Badge>
                      )}
                      {comment.accession.org && (
                        <Text muted size="1">
                          {comment.accession.org.name}
                        </Text>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between border-t px-4 py-3 shrink-0">
        <Text muted size="1">
          {data?.totalCount ?? 0} comments
        </Text>
        <Pagination
          page={page}
          pageCount={pageCount}
          onPageChange={onPageChange}
        />
      </div>
    </>
  );
}

function CommenterHoverCard({
  name,
  email,
  orgName,
}: {
  name: string;
  email?: string | null;
  orgName?: string | null;
}) {
  return (
    <div className="invisible absolute left-0 top-full z-50 pt-1 group-hover/avatar:visible">
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: hover card interaction */}
      <div
        className="rounded-lg border bg-popover px-3 py-2 text-sm shadow-md min-w-[180px] space-y-1"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <p className="font-medium text-popover-foreground">{name}</p>
        {email && (
          <span
            role="link"
            tabIndex={0}
            className="flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.location.href = `mailto:${email}`;
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `mailto:${email}`;
              }
            }}
          >
            <MailIcon className="size-3 shrink-0" />
            {email}
          </span>
        )}
        {orgName && <p className="text-xs text-muted-foreground">{orgName}</p>}
      </div>
    </div>
  );
}
